<script setup lang="ts">
import { ref, onBeforeMount, watch, computed } from "vue";
import BexTodoDO = Modules.BookingExperts.Data.BexTodoDO;
import { useApiClient } from "@/api/client";
import AddEditTodo from "@/modules/pro_loyalty/components/AddEditTodo.vue";
import useDialog from "@/ui/composables/useDialog";
import { toastApiResult } from "@/toaster";

const getEmptyTodo = (): BexTodoDO => {
	return {
		uuid: null,
		name: "",
		details: "",
		quantity: 1,
		due_type: "on_arrival",
		is_blocker: false,
		is_checkable: false,
		is_taxable: false,
		is_urgent: false,
		administration_todo_lists: {},
	};
};

const currentPage = ref<number>(1);
const itemsPerPage = ref<number>(15);
const totalItems = ref<number>(1);
const bexTodos = ref<BexTodoDO[]>();
const isLoading = ref<boolean>(true);
const isTodoDialogVisible = ref<boolean>(false);
const BexTodoToEdit = ref<BexTodoDO>();

const getBexTodos = async () => {
	isLoading.value = true;
	const response = await useApiClient("proLoyalty").getTodos({
		page: currentPage.value,
		per_page: itemsPerPage.value,
		sortBy: [],
	});
	if (response.isOk()) {
		bexTodos.value = response.value.data;
		totalItems.value = response.value.total;
	}
	isLoading.value = false;
};

const addNewTodo = () => {
	BexTodoToEdit.value = getEmptyTodo();
	isTodoDialogVisible.value = true;
};

const editTodo = (bexTodo: BexTodoDO) => {
	BexTodoToEdit.value = bexTodo;
	isTodoDialogVisible.value = true;
};

const noOfPages = computed(() =>
	Math.ceil(totalItems.value / itemsPerPage.value)
);

const removeBexTodo = async (bexTodo: BexTodoDO) => {
	const confirm = await useDialog().confirm({
		title: "Delete Task",
		message: `Are you sure you want to delete the Task?`,
		isDelete: true,
	});

	if (confirm) {
		const response = await toastApiResult(
			useApiClient("proLoyalty").removeTodo(bexTodo)
		);
		if (response.isOk()) {
			getBexTodos();
		}
	}
};

watch(currentPage, () => getBexTodos());
onBeforeMount(() => getBexTodos());
</script>

<template>
	<add-edit-todo
		v-model="isTodoDialogVisible"
		v-if="isTodoDialogVisible"
		:bex-todo="BexTodoToEdit"
		@refresh-todos="getBexTodos"
	/>
	<v-card class="px-5 mt-10">
		<div class="d-flex my-5">
			<v-spacer />
			<v-btn color="primary" variant="flat" @click="addNewTodo()">
				Add new Task
			</v-btn>
		</div>

		<template v-for="bexTodo in bexTodos" v-if="!isLoading">
			<v-card class="pa-5 mb-5 d-flex">
				<div class="d-flex align-center">
					<div>
						<v-card-title>{{ bexTodo.quantity }} x</v-card-title>
					</div>
				</div>
				<v-divider class="mx-5" vertical />
				<div>
					<v-card-subtitle class="pa-0">Name: </v-card-subtitle>
					<div class="bold">{{ bexTodo.name }}</div>
				</div>
				<v-divider class="mx-5" vertical />
				<div>
					<v-card-subtitle class="pa-0">Due: </v-card-subtitle>
					<div>{{ $t(`pro_loyalty.due_types.${bexTodo.due_type}`) }}</div>
				</div>
				<v-divider class="mx-5" vertical />
				<div>
					<v-card-subtitle class="pa-0">Flags: </v-card-subtitle>
					<v-chip
						v-if="bexTodo.is_blocker"
						class="mr-3"
						density="compact"
						variant="tonal"
						color="on-surface"
						>Blocker</v-chip
					>
					<v-chip
						v-if="bexTodo.is_checkable"
						class="mr-3"
						density="compact"
						variant="tonal"
						color="on-surface"
						>Checkable</v-chip
					>
					<v-chip
						v-if="bexTodo.is_taxable"
						class="mr-3"
						density="compact"
						variant="tonal"
						color="on-surface"
						>Taxable</v-chip
					>
					<v-chip
						v-if="bexTodo.is_urgent"
						class="mr-3"
						density="compact"
						variant="tonal"
						color="on-surface"
						>Urgent</v-chip
					>
				</div>
				<v-spacer />
				<div class="d-flex align-center">
					<div>
						<v-btn
							icon
							color="on-surface"
							variant="text"
							@click="editTodo(bexTodo)"
						>
							<i class="fa-light fa-pencil"></i
						></v-btn>
						<v-btn
							icon
							color="error"
							variant="text"
							@click="removeBexTodo(bexTodo)"
						>
							<i class="fa-light fa-trash-alt"></i
						></v-btn>
					</div>
				</div>
			</v-card>
		</template>
		<template v-else>
			<v-progress-linear indeterminate color="primary" size="4" />
		</template>

		<v-pagination
			v-model="currentPage"
			density="compact"
			:length="noOfPages"
			class="my-5"
		></v-pagination>
	</v-card>
</template>

<style scoped lang="scss">
.bold {
	font-weight: bold;
}
</style>
